import React, { useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import Panel from '../Panel';
import { dateFormat, EntityType, RateCategory, } from '../constants';
import { List, Form, Message } from 'semantic-ui-react';
import Weekdays from '../Weekdays';
import moment from "moment";
import { CancelButton, CheckButton, EditButton, TrashButton, CopyButton } from '../icons';
import { PriceListContext } from '../../../pages/PriceList/context';
import { DatePicker } from '../DatePicker';
import { useTextResources } from '../../../language';
import WeekDaysSelect from '../WeekDaysSelect';
import { getArrWeekdays, getIntWeekdays, handleStartEndChange, isCovered, validateDateRange, validateDateRangeMessage } from '../utils';
import { defaultRPEcopyElement } from '../../../pages/PriceList/Pricing/pricingUtils.jsx'

const PricingElementPanel = ({ pricing, isDerived, onDelete, onSave, onCopy, children, setPricings }) => {
    const text = useTextResources();
    const { state } = useContext(PriceListContext);
    const [startDate, setStartDate] = useState(pricing.StartDate);
    const [endDate, setEndDate] = useState(pricing.EndDate);
    const [selectedDays, setSelectedDays] = useState(getArrWeekdays(pricing.Weekdays));

    useEffect(() => {
        setStartDate(pricing.StartDate);
        setEndDate(pricing.EndDate);
        setSelectedDays(getArrWeekdays(pricing.Weekdays));
    }, [pricing])

    const isCopy = () => !pricing.StartDate || !pricing.EndDate;
    const saveClicked = () => {
        onSave({ 
            ...pricing,
            prev: pricing,
            isEdit: true, 
            isCopy: false,
            IsUpdated: true, 
            StartDate: moment(startDate).startOf("day").utcOffset(0, true).toISOString(),
            EndDate:  moment(endDate).startOf("day").utcOffset(0, true).toISOString(), 
            Weekdays: getIntWeekdays(selectedDays) 
        });
    }

    const setIsEditing = (isEditing) => {
        setPricings(pricings => {
            if (Array.isArray(pricings)) {
                return pricings.map(p => p.Id === pricing.Id ? { ...p, isEdit: isEditing } : p);
            } else if (typeof pricings === 'object') {
                return Object.keys(pricings).reduce((acc, key) => {
                    acc[key] = pricings[key].Id === pricing.Id ? { ...pricings[key], isEdit: isEditing } : pricings[key];
                    return acc;
                }, {});
            }
            else {
                console.error("Invalid pricings format");
                return pricings;
            }
        });
    }

    const editClicked = () => {
        setIsEditing(true);
    }

    const cancelClicked = () => {
        if (isCopy()) {
            onDelete();
            return;
        }

        setStartDate(pricing.StartDate);
        setEndDate(pricing.EndDate);
        setSelectedDays(getArrWeekdays(pricing.Weekdays));
        setIsEditing(false);
    }

    const copyClicked = () => {
        const newPricing = defaultRPEcopyElement({ ...pricing, isEdit: true, TempId: `copyPricingTempId-${crypto.randomUUID()}` });
        onCopy(pricing, newPricing);
    }
    

    let elements = [];
    if (state.RateCategoryId === RateCategory.Supplement) {
        const standardRoomType = state.RoomTypes.find(x => x.DisplayOrder === 0);
        elements = state.RatePricingElements
            .filter(x => x.EntityTypeId === EntityType.RoomType && x.EntityId !== standardRoomType?.Id);
    } else {
        elements = state.RatePricingElements?.filter(r =>
            r.EntityTypeId === pricing.EntityTypeId &&
            r.EntityId === pricing.EntityId &&
            r.RateId === state.Id &&
            r.IsActive &&
            !r.IsDummy);
    }
    const hasOwnElements = state.RateCategoryId !== RateCategory.Derived || elements.length > 0;
    const coverage = isCovered(state, elements)
    const asDate = (date) => moment(date).format(dateFormat)
    const startStyle = ()=>{
        if (
            coverage.hasStartGap && 
            coverage.earliestStart.isSame(startDate, 'day') && 
            hasOwnElements
        ) {
            return { color: "#F2711C" };
        }
        else {
            return null;
        }
    }
    const endStyle = ()=>{
        if (
            coverage.hasEndGap && 
            coverage.latestEnd.isSame(endDate, 'day') && 
            hasOwnElements
        ) {
            return { color: "#F2711C" };
        }
        else {
            return null;
        }
    }

    const panelDerivedClassName = isDerived ? "derived-pricing" : "";
    return (
        <Panel
            className={`pricing-element-panel ${panelDerivedClassName}`}
            color={pricing.IsNew ? "red" : isDerived ? "green" : undefined}
            header={!pricing.isEdit ?
                <List.Description style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                        <span style={startStyle()}>
                            {asDate(startDate)}
                        </span>
                        {" - "}
                        <span style={endStyle()}>
                            {asDate(endDate)}
                        </span>
                        <span style={{ marginLeft: 10 }}><Weekdays importedSelectedWeekDaysArray={selectedDays} /></span>
                    </div>
                    <div>
                        <TrashButton disabled={isDerived} content={"Delete"} onClick={onDelete} floated="right" />
                        <EditButton disabled={isDerived} content={"Edit"} onClick={editClicked} floated="right" style={{ marginRight: '1rem' }} />
                        <CopyButton disabled={isDerived} content={"Copy"} onClick={copyClicked} floated="right" style={{ marginRight: '1rem' }} />
                    </div>
                </List.Description> :
                <List.Description>
                    <Form warning={!validateDateRange(startDate, endDate, selectedDays)}>
                        <Form.Group widths={4} className="date-range-adding-edit">

                            <DatePicker
                                minDate={moment(state.StartDate).startOf("day").toDate()}
                                maxDate={new Date(state.EndDate)}
                                tooltipContent={text('From')}
                                onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
                                clearable={isCopy()}
                                value={startDate ? new Date(startDate) : startDate}
                            />
                            <DatePicker
                                minDate={moment(startDate).startOf("day").toDate()}
                                maxDate={new Date(state.EndDate)}
                                tooltipContent={text('To')}
                                onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
                                clearable={isCopy()} value={endDate ? new Date(endDate) : endDate}
                            />
                            <WeekDaysSelect onChange={setSelectedDays} initDays={selectedDays} useWithoutDates />
                            <CheckButton size='large' disabled={!validateDateRange(startDate, endDate, selectedDays)} onClick={saveClicked} floated="right" />
                            <CancelButton size='large' onClick={cancelClicked} floated="right" style={{ marginRight: '1rem' }} />
                        </Form.Group>
                        <Message
                            warning
                            content={text(validateDateRangeMessage(startDate, endDate, selectedDays))}
                        />
                    </Form>
                </List.Description>
            }
        >
            {children}
        </Panel>
    );
}

PricingElementPanel.propTypes = {
    pricing: PropTypes.object,
    onDelete: PropTypes.func,
    onSave: PropTypes.func,
    onCopy: PropTypes.func,
    isDerived: PropTypes.bool,
    children: PropTypes.any,
    setPricings: PropTypes.func,
}
export default PricingElementPanel
